import React from 'react';

import { useIntl } from 'react-intl-next';

import { FilterProductIcon } from '../../../../../common/ui/filters/filter-product-icon';
import type { FilterProps } from '../../../../../common/ui/filters/product-filters/types';
import SelectFilter from '../../../../../common/ui/filters/select-filter';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import { useFiltersDiscoverability2Enabled } from '../../../../../controllers/store/bootstrap';
import { usePrimaryProduct } from '../../../../../controllers/store/bootstrap/accessors';
import { filter as labelFilter } from '../../../../../controllers/store/search/filters/label';

import { i18n } from './messages';

const LabelFilter = ({
	isFilterInDropdown,
	showPrimaryProductIcon,
	fit = 'default',
	onFilterChange,
	hideOnHoverOut,
	onClearOptions,
}: FilterProps): React.JSX.Element | null => {
	const [primaryProduct] = usePrimaryProduct();
	const { formatMessage } = useIntl();
	const isFiltersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const [
		filterData,
		{ loadInitialOptions, setLookupInput, setOption, clearLookupInput, clearOptions },
	] = useSelectFilter({
		filterId: labelFilter.id,
	});

	if (!filterData) {
		return null;
	}

	const {
		isLoading,
		suggestedOptions,
		selectedOptions,
		isLookupDefined,
		lookupInput,
		isLookupActive,
		options,
	} = filterData;

	const groupedOptions = [
		{
			label: formatMessage(i18n.selectedOptionsSection),
			options: selectedOptions,
		},
		{
			label: formatMessage(i18n.suggestedOptionsSection),
			options: suggestedOptions,
		},
	];

	const label = fit === 'compact' && selectedOptions.length > 0 ? '' : formatMessage(i18n.label);

	return (
		<SelectFilter
			contentFit={fit}
			canClearFilter
			checkboxIndicators
			onDropdownOpen={loadInitialOptions}
			onDropdownClose={clearLookupInput}
			onSearchInputChange={setLookupInput}
			onSelectChange={(option, ...restArgs) => {
				setOption(option, ...restArgs);
				onFilterChange?.(option);
			}}
			options={isLookupActive ? options : groupedOptions}
			selectedOptions={selectedOptions}
			label={label}
			noOptionsMessage={formatMessage(i18n.confluenceFilterLabelsNoText)}
			placeholder={formatMessage(i18n.confluenceFilterLabelPlaceholder)}
			filterOrControlId={'label'}
			isLoading={isLoading}
			isSearchable={isLookupDefined}
			searchInput={lookupInput}
			onClearOptions={() => {
				clearOptions();
				onClearOptions?.({ filterId: labelFilter.id });
			}}
			isFilterInDropdown={isFilterInDropdown}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
			iconBefore={
				<FilterProductIcon
					showPrimaryProductIcon={showPrimaryProductIcon}
					primaryProduct={primaryProduct}
				/>
			}
			hideOnHoverOut={hideOnHoverOut}
		/>
	);
};

export default LabelFilter;
